/**
 * Pure rendering functions for the Leo's Quest game.
 * These have no mutable state — they only draw to the canvas given data.
 */

import type { Dir, Fireball, Boom, Vec } from './types';
import {
  W, H, HUD_H, PLAY_TOP, PLAY_BOT,
  PW, PH, FB, BOOM_DURATION,
} from './constants';

// ── Pixel-letter patterns for the "LEO" title (6 cols × 9 rows) ──

const TITLE_LETTERS: Record<string, number[][]> = {
  L: [
    [1,1,0,0,0,0],[1,1,0,0,0,0],[1,1,0,0,0,0],
    [1,1,0,0,0,0],[1,1,0,0,0,0],[1,1,0,0,0,0],
    [1,1,0,0,0,0],[1,1,1,1,1,1],[1,1,1,1,1,1],
  ],
  E: [
    [1,1,1,1,1,1],[1,1,1,1,1,1],[1,1,0,0,0,0],
    [1,1,1,1,1,0],[1,1,1,1,1,0],[1,1,0,0,0,0],
    [1,1,0,0,0,0],[1,1,1,1,1,1],[1,1,1,1,1,1],
  ],
  O: [
    [0,1,1,1,1,0],[1,1,1,1,1,1],[1,1,0,0,1,1],
    [1,1,0,0,1,1],[1,1,0,0,1,1],[1,1,0,0,1,1],
    [1,1,0,0,1,1],[1,1,1,1,1,1],[0,1,1,1,1,0],
  ],
};

function drawPixelBlock(
  ctx: CanvasRenderingContext2D, pattern: number[][],
  x: number, y: number, cell: number, color: string,
) {
  ctx.fillStyle = color;
  for (let r = 0; r < pattern.length; r++) {
    for (let c = 0; c < pattern[r].length; c++) {
      if (pattern[r][c]) ctx.fillRect(x + c * cell, y + r * cell, cell, cell);
    }
  }
}

function drawTitle(ctx: CanvasRenderingContext2D, cx: number, y: number, cell: number) {
  const word = 'LEO';
  const letterW = 6 * cell;
  const gap = cell;
  const totalW = word.length * letterW + (word.length - 1) * gap;
  let ox = cx - totalW / 2;

  for (const ch of word) {
    const p = TITLE_LETTERS[ch];
    if (!p) continue;
    drawPixelBlock(ctx, p, ox + 1, y + 1, cell, '#8B6914');
    drawPixelBlock(ctx, p, ox, y, cell, '#FFD700');
    ctx.fillStyle = '#FFF8DC';
    for (let c = 0; c < p[0].length; c++) {
      if (p[0][c]) ctx.fillRect(ox + c * cell, y, cell, 1);
    }
    ox += letterW + gap;
  }
}

function drawVines(ctx: CanvasRenderingContext2D) {
  const shades = ['#0d3d07', '#1a5c0a', '#2d8a15', '#1a5c0a'];
  for (let y = 0; y < H; y += 4) {
    const lw = 16 + Math.sin(y * 0.09) * 5 + Math.sin(y * 0.21) * 3;
    const rw = 16 + Math.sin(y * 0.09 + 1.5) * 5 + Math.sin(y * 0.18) * 3;
    ctx.fillStyle = shades[Math.floor(y / 4) % shades.length];
    ctx.fillRect(0, y, lw, 4);
    ctx.fillRect(W - rw, y, rw, 4);
    if (y % 16 === 0) {
      ctx.fillStyle = '#2d8a15';
      ctx.fillRect(lw, y, 3, 3);
      ctx.fillRect(W - rw - 3, y + 1, 3, 3);
    }
  }
  for (let x = 0; x < W; x += 4) {
    const th = 10 + Math.sin(x * 0.12) * 3;
    const bh = 10 + Math.sin(x * 0.12 + 1) * 3;
    ctx.fillStyle = shades[Math.floor(x / 4) % shades.length];
    ctx.fillRect(x, 0, 4, th);
    ctx.fillRect(x, H - bh, 4, bh);
  }
}

// ── Intro screen ──

export function renderIntro(
  ctx: CanvasRenderingContext2D,
  totalTime: number, blink: number, stars: Vec[],
) {
  ctx.fillStyle = '#0a0a1a';
  ctx.fillRect(0, 0, W, H);

  // Twinkling stars
  for (const s of stars) {
    if (Math.sin(totalTime * 2.5 + s.x * 0.3 + s.y * 0.7) > 0.4) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(s.x, s.y, 1, 1);
    }
  }

  drawVines(ctx);

  ctx.fillStyle = '#b0b0b0';
  ctx.font = '7px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('THE LEGEND OF', W / 2, 50);

  drawTitle(ctx, W / 2, 64, 4);
  drawStaffDecoration(ctx, W / 2, 108, totalTime);

  ctx.fillStyle = '#555555';
  ctx.font = '6px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('\u00A9 LEO ADS AI', W / 2, 170);

  if (Math.sin(blink * 3) > 0) {
    ctx.fillStyle = '#FFD700';
    ctx.font = '7px monospace';
    ctx.fillText('PRESS ENTER TO START', W / 2, 192);
  }
}

function drawStaffDecoration(
  ctx: CanvasRenderingContext2D, sx: number, sy: number, totalTime: number,
) {
  ctx.strokeStyle = '#DAA520';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx, sy + 28);
  ctx.stroke();
  ctx.lineWidth = 1;

  ctx.fillStyle = Math.sin(totalTime * 4) > 0 ? '#00FFFF' : '#88FFFF';
  ctx.fillRect(sx - 3, sy - 3, 7, 7);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(sx - 1, sy - 1, 3, 3);

  if (Math.sin(totalTime * 5) > 0.2) {
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillRect(sx - 7, sy, 3, 1);
    ctx.fillRect(sx + 5, sy, 3, 1);
    ctx.fillRect(sx, sy - 7, 1, 3);
    ctx.fillRect(sx, sy + 5, 1, 3);
  }
}

// ── Sprite drawing ──

export function drawWizard(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, dir: Dir, frame: number, totalTime: number,
) {
  drawWizardBody(ctx, x, y, frame);
  drawWizardStaff(ctx, x, y, dir, totalTime);
}

function drawWizardBody(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  // Hat
  ctx.fillStyle = '#6B28B8';
  ctx.fillRect(x + 2, y + 1, 8, 2);
  ctx.fillRect(x + 3, y - 1, 6, 2);
  ctx.fillRect(x + 4, y - 3, 4, 2);
  ctx.fillRect(x + 5, y - 5, 2, 2);
  ctx.fillStyle = '#FFD700';
  ctx.fillRect(x + 5, y, 2, 1);

  // Head + eyes
  ctx.fillStyle = '#FFDAB9';
  ctx.fillRect(x + 3, y + 3, 6, 4);
  ctx.fillStyle = '#000';
  ctx.fillRect(x + 4, y + 4, 1, 1);
  ctx.fillRect(x + 7, y + 4, 1, 1);

  // Beard
  ctx.fillStyle = '#CCC';
  ctx.fillRect(x + 4, y + 7, 4, 2);
  ctx.fillRect(x + 5, y + 9, 2, 1);

  // Robe + belt
  ctx.fillStyle = '#4B0082';
  ctx.fillRect(x + 2, y + 7, 8, 5);
  ctx.fillStyle = '#6A0DAD';
  ctx.fillRect(x + 3, y + 8, 6, 3);
  ctx.fillStyle = '#DAA520';
  ctx.fillRect(x + 3, y + 8, 6, 1);

  // Feet
  ctx.fillStyle = '#4B0082';
  const fOff = frame === 1 ? 1 : 0;
  ctx.fillRect(x + 2 + fOff, y + 12, 3, 2);
  ctx.fillRect(x + 7 - fOff, y + 12, 3, 2);
}

function drawWizardStaff(
  ctx: CanvasRenderingContext2D, x: number, y: number, dir: Dir, totalTime: number,
) {
  const scx = x + PW / 2;
  const scy = y + PH / 2;
  ctx.fillStyle = '#DAA520';

  const staffParts: Record<Dir, { shaft: number[]; gem: number[] }> = {
    right: { shaft: [scx + 3, scy - 1, 8, 2], gem: [scx + 10, scy - 2, 3, 3] },
    left:  { shaft: [scx - 11, scy - 1, 8, 2], gem: [scx - 13, scy - 2, 3, 3] },
    up:    { shaft: [scx - 1, scy - 11, 2, 8], gem: [scx - 2, scy - 13, 3, 3] },
    down:  { shaft: [scx - 1, scy + 3, 2, 8], gem: [scx - 2, scy + 10, 3, 3] },
  };

  const { shaft, gem } = staffParts[dir];
  ctx.fillRect(shaft[0], shaft[1], shaft[2], shaft[3]);
  ctx.fillStyle = '#00FFFF';
  ctx.fillRect(gem[0], gem[1], gem[2], gem[3]);

  if (Math.sin(totalTime * 10) > 0.6) {
    ctx.fillStyle = '#FFF';
    const sparkle: Record<Dir, number[]> = {
      right: [scx + 11, scy - 1], left: [scx - 12, scy - 1],
      up: [scx - 1, scy - 12], down: [scx - 1, scy + 11],
    };
    ctx.fillRect(sparkle[dir][0], sparkle[dir][1], 1, 1);
  }
}

export function drawBusinessman(
  ctx: CanvasRenderingContext2D, x: number, y: number, frame: number,
) {
  // Head + hair + angry brows
  ctx.fillStyle = '#FFDAB9';
  ctx.fillRect(x + 2, y, 6, 5);
  ctx.fillStyle = '#333';
  ctx.fillRect(x + 2, y, 6, 2);
  ctx.fillStyle = '#000';
  ctx.fillRect(x + 3, y + 2, 1, 1);
  ctx.fillRect(x + 6, y + 2, 1, 1);
  ctx.fillRect(x + 3, y + 1, 2, 1);
  ctx.fillRect(x + 5, y + 1, 2, 1);

  // Suit + shirt + tie
  ctx.fillStyle = '#2c2c2c';
  ctx.fillRect(x + 1, y + 5, 8, 6);
  ctx.fillStyle = '#DDD';
  ctx.fillRect(x + 4, y + 5, 2, 4);
  ctx.fillStyle = '#CC0000';
  ctx.fillRect(x + 4, y + 5, 1, 4);

  // Legs
  ctx.fillStyle = '#1a1a1a';
  const fOff = frame === 1 ? 1 : 0;
  ctx.fillRect(x + 1 + fOff, y + 11, 3, 3);
  ctx.fillRect(x + 6 - fOff, y + 11, 3, 3);

  // Briefcase
  ctx.fillStyle = '#8B4513';
  ctx.fillRect(x + 9, y + 7, 3, 4);
  ctx.fillStyle = '#DAA520';
  ctx.fillRect(x + 10, y + 7, 1, 1);
}

// ── HUD + environment ──

export function renderPlayfield(
  ctx: CanvasRenderingContext2D, bushes: Vec[],
  level: number, score: number, aliveCount: number,
) {
  // Grass
  ctx.fillStyle = '#3a7d28';
  ctx.fillRect(0, PLAY_TOP, W, PLAY_BOT - PLAY_TOP);
  ctx.fillStyle = '#2d6a1e';
  for (let i = 0; i < 40; i++) {
    ctx.fillRect((i * 37 + 13) % W, PLAY_TOP + ((i * 53 + 7) % (PLAY_BOT - PLAY_TOP)), 3, 3);
  }
  ctx.fillStyle = '#4a9632';
  for (let i = 0; i < 20; i++) {
    ctx.fillRect((i * 61 + 29) % W, PLAY_TOP + ((i * 43 + 17) % (PLAY_BOT - PLAY_TOP)), 2, 2);
  }

  // Bushes
  for (const b of bushes) {
    ctx.fillStyle = '#1a5c0a';
    ctx.fillRect(b.x, b.y, 8, 6);
    ctx.fillStyle = '#2d8a15';
    ctx.fillRect(b.x + 1, b.y + 1, 6, 4);
    ctx.fillStyle = '#4aad22';
    ctx.fillRect(b.x + 3, b.y + 2, 2, 2);
  }

  // HUD
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, W, HUD_H);
  ctx.fillStyle = '#FFF';
  ctx.font = '8px monospace';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(`LVL ${level}`, 4, 5);
  ctx.textAlign = 'center';
  ctx.fillText(`${aliveCount} LEFT`, W / 2, 5);
  ctx.textAlign = 'right';
  ctx.fillText(`${score}`, W - 4, 5);
}

export function renderFireballs(ctx: CanvasRenderingContext2D, fireballs: Fireball[]) {
  for (const f of fireballs) {
    ctx.fillStyle = '#FF4500';
    ctx.fillRect(f.x - 1, f.y - 1, FB + 2, FB + 2);
    ctx.fillStyle = '#FFD700';
    ctx.fillRect(f.x, f.y, FB, FB);
    ctx.fillStyle = '#FFF';
    ctx.fillRect(f.x + 1, f.y + 1, 2, 2);
  }
}

export function renderExplosions(ctx: CanvasRenderingContext2D, booms: Boom[]) {
  for (const b of booms) {
    const progress = 1 - b.t / BOOM_DURATION;
    const r = progress * 10;
    const alpha = 1 - progress;
    ctx.fillStyle = `rgba(255,102,0,${alpha})`;
    ctx.fillRect(b.x - r, b.y - r, r * 2, r * 2);
    ctx.fillStyle = `rgba(255,215,0,${alpha * 0.7})`;
    ctx.fillRect(b.x - r * 0.4, b.y - r * 0.4, r * 0.8, r * 0.8);
  }
}

// ── Overlay screens ──

export function renderGameOverOverlay(
  ctx: CanvasRenderingContext2D, score: number, level: number, blink: number,
) {
  ctx.fillStyle = 'rgba(0,0,0,0.75)';
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#FF0000';
  ctx.font = 'bold 16px monospace';
  ctx.fillText('GAME OVER', W / 2, H / 2 - 24);
  ctx.fillStyle = '#FFF';
  ctx.font = '8px monospace';
  ctx.fillText(`SCORE: ${score}`, W / 2, H / 2);
  ctx.fillText(`REACHED LEVEL ${level}`, W / 2, H / 2 + 14);
  if (Math.sin(blink * 3) > 0) {
    ctx.fillStyle = '#FFD700';
    ctx.font = '7px monospace';
    ctx.fillText('PRESS ENTER TO RESTART', W / 2, H / 2 + 36);
  }
}

export function renderLevelUpOverlay(ctx: CanvasRenderingContext2D, level: number) {
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#FFD700';
  ctx.font = 'bold 12px monospace';
  ctx.fillText(`LEVEL ${level} COMPLETE!`, W / 2, H / 2 - 8);
  ctx.fillStyle = '#FFF';
  ctx.font = '8px monospace';
  ctx.fillText(`GET READY FOR LEVEL ${level + 1}`, W / 2, H / 2 + 12);
}
